import type { WireAuthLink, WireAuthProviderAdminRecord } from '#layers/access/utils/auth-providers/wire';
import { computed, readonly, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuth } from '#layers/access/stores/auth';
import { AuthProtocol } from '#layers/access/types/auth-providers/define';
import { isMissingEndpointError } from '#layers/access/utils/auth-providers/is-missing-endpoint-error';
import { getDisplayName } from '#layers/access/utils/auth-providers/registry';
import { useSystemEntityStore } from '#layers/runtime/public';
import { useToastResultHandling } from '#layers/ui/public';
import { useLoginProviders } from './use-login-providers';

export interface AccountConnectionEntry {
	// DList requires `id` on each item.
	id: string;
	provider: WireAuthProviderAdminRecord;
	link: WireAuthLink | null;
	// True when this is a linked entry AND disconnecting it would leave the user
	// with no other known sign-in method (no other linked SSO providers AND no
	// active org password provider).
	isLastMethod: boolean;
}

/**
 * Surface of the current user's SSO links, joined with the org's configured
 * providers. Drives the "Connected sign-in methods" section on the Account →
 * Access page.
 *
 * Provider list comes from the admin endpoint (only field that exposes `id`,
 * which we need to join on). The user's `authLinks` are hydrated by
 * `auth.fetchCurrentUser()` (which selects `authLinks.providerId` on the
 * `/system/users/me` call) — that route is per-user, so non-admin users get
 * their own links even though they can't read the admin provider list.
 */
export function useAccountConnections() {
	const store = useSystemEntityStore();
	const auth = useAuth();
	const i18n = useI18n();
	const { t } = i18n;

	const inFlightApiNames = ref(new Set<string>());

	const connectToast = useToastResultHandling();
	const disconnectToast = useToastResultHandling();

	const { data: providersData, refresh: refreshProviders, loading: providersLoading, error: providersError } =
		store.MonospaceAuthProvider.query((q) => q.many({}));

	// Public, anonymous-readable list. Used as a fallback for non-admin users
	// who can't read the admin provider list above.
	const { entries: publicProviders } = useLoginProviders();

	const linksData = computed<WireAuthLink[]>(() => auth.currentUser?.authLinks ?? []);

	// Mirror of the `entries` filter. For non-admin users the admin list comes
	// back empty (admin-gated), so fall back to the public `/auth/providers`
	// list — that endpoint is anon-readable and surfaces the active password
	// provider so the "Change password" section still renders.
	const hasLocalPassword = computed(() => {
		const admin = (providersData.value ?? []) as WireAuthProviderAdminRecord[];

		if (admin.length > 0) {
			return admin.some((p) => p.protocol === AuthProtocol.Password && p.state.kind === 'active');
		}

		return publicProviders.value.some((p) => p.protocol === AuthProtocol.Password);
	});

	const entries = computed<AccountConnectionEntry[]>(() => {
		const providers = (providersData.value ?? []) as WireAuthProviderAdminRecord[];
		const links = linksData.value;

		const base = providers
			.filter((provider) => provider.protocol !== AuthProtocol.Password && provider.state.kind === 'active')
			.map((provider) => ({
				id: provider.id,
				provider,
				link: links.find((link) => link.providerId === provider.id) ?? null,
			}));

		// Only entry is a guard against disconnecting the user's last sign-in
		// method: true when this linked entry is the sole linked SSO provider and
		// there's no active org password provider to fall back on.
		const linkedCount = base.filter((entry) => entry.link !== null).length;

		return base.map((entry) => ({
			...entry,
			isLastMethod: entry.link !== null && linkedCount === 1 && !hasLocalPassword.value,
		}));
	});

	async function refresh(): Promise<void> {
		await Promise.all([refreshProviders(), auth.fetchCurrentUser()]);
	}

	async function connect(provider: WireAuthProviderAdminRecord): Promise<void> {
		// Guard re-entrant clicks: the store starts a POST + browser redirect, so
		// a double-click before the redirect fires could send two POSTs. The
		// composable owns the toast — callers don't need to handle errors again.
		if (inFlightApiNames.value.has(provider.apiName)) return;
		inFlightApiNames.value.add(provider.apiName);

		try {
			await auth.startLinkFlow(provider);
			// No success toast — `window.location.href` redirects before we get here.
		}
		catch (error) {
			connectToast.onError(error, {
				title: t('account.settings.connections.connect_failed', { provider: getDisplayName(provider, i18n) }),
			});

			inFlightApiNames.value.delete(provider.apiName);
		}
	}

	async function disconnect(provider: WireAuthProviderAdminRecord): Promise<void> {
		if (inFlightApiNames.value.has(provider.apiName)) return;
		const name = getDisplayName(provider, i18n);
		inFlightApiNames.value.add(provider.apiName);

		try {
			await auth.unlinkProvider(provider);

			disconnectToast.onSuccess({
				title: t('account.settings.connections.disconnect_success', { provider: name }),
			});
		}
		catch (error) {
			disconnectToast.onError(error, {
				title: t('account.settings.connections.disconnect_failed', { provider: name }),
			});

			return;
		}
		finally {
			inFlightApiNames.value.delete(provider.apiName);
		}

		// Refresh AFTER the success toast. The BE already deleted the row, so
		// a failed refresh just means the UI shows stale data until the next
		// interaction — log it in dev, otherwise stay quiet.
		try {
			await auth.fetchCurrentUser();
		}
		catch (refreshError) {
			if (import.meta.dev) {
				console.warn('[useAccountConnections] post-disconnect fetchCurrentUser failed', refreshError);
			}
		}
	}

	const error = computed(() => {
		// 404 on the admin providers endpoint means the SSO entitlement isn't
		// configured for this org — surface as "no methods" rather than an error.
		if (isMissingEndpointError(providersError.value)) return null;
		return providersError.value ?? null;
	});

	const isLoading = computed(() => providersLoading.value);

	return {
		entries: readonly(entries),
		hasLocalPassword: readonly(hasLocalPassword),
		isLoading: readonly(isLoading),
		error: readonly(error),
		inFlightApiNames: readonly(inFlightApiNames),
		refresh,
		connect,
		disconnect,
	};
}
